import { QuestionnaireSourceRecordDetailDialog } from '@/components/Customers/QuestionnaireSourceRecordDetailDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  QuestionnaireSourceRecordArchiveStatus,
  QuestionnaireSourceRecordArchiveStatusFilter,
  QuestionnaireSourceRecordContract,
  QuestionnaireSourceRecordLinkStatus,
} from '@/contracts/customer-application-binding'
import {
  archiveQuestionnaireSourceRecord,
  listQuestionnaireSourceRecords,
} from '@/lib/data-client/customers'
import { cn } from '@/lib/utils'
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  CheckCircle2,
  Eye,
  Link2,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

type LinkStatusFilter = 'all' | QuestionnaireSourceRecordLinkStatus
type ArchiveStatusFilter = QuestionnaireSourceRecordArchiveStatusFilter

const linkStatusMeta: Record<
  QuestionnaireSourceRecordLinkStatus,
  { label: string; variant: 'success' | 'warning' | 'secondary'; icon: typeof CheckCircle2 }
> = {
  available: { label: '可建立申請', variant: 'success', icon: CheckCircle2 },
  linked: { label: '已建立申請', variant: 'warning', icon: Link2 },
  unavailable: { label: '不可建立', variant: 'secondary', icon: AlertTriangle },
}

const linkedApplicationStatusLabels: Record<string, string> = {
  draft: '草稿',
  pending_review: '待主管審核',
  changes_requested: '主管退回',
  approved: '主管核准',
  approved_pending_erp_filing: '審核通過等待建檔',
  customer_created: '客戶已建檔',
  duplicate_pending: '重複待處理',
  rejected: '已拒絕',
}

export function CustomerApplicationSourceRecordPicker({
  selectedId,
  onSelect,
  className,
}: {
  selectedId?: string
  onSelect: (record: QuestionnaireSourceRecordContract) => void
  className?: string
}) {
  const [keyword, setKeyword] = useState('')
  const [linkStatus, setLinkStatus] = useState<LinkStatusFilter>('available')
  const [archiveStatus, setArchiveStatus] = useState<ArchiveStatusFilter>('active')
  const [records, setRecords] = useState<QuestionnaireSourceRecordContract[]>([])
  const [detailRecord, setDetailRecord] = useState<QuestionnaireSourceRecordContract | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [archivingRecordId, setArchivingRecordId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadRecords = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await listQuestionnaireSourceRecords({
        keyword,
        linkStatus,
        archiveStatus,
        limit: 100,
      })
      setRecords(result.data)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '無法載入問卷記錄。')
    } finally {
      setIsLoading(false)
    }
  }, [archiveStatus, keyword, linkStatus])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadRecords()
    }, 150)
    return () => window.clearTimeout(timeoutId)
  }, [loadRecords])

  const totalAvailable = useMemo(
    () =>
      records.filter(
        (record) => record.linkStatus === 'available' && record.archiveStatus !== 'archived',
      ).length,
    [records],
  )

  const updateArchiveStatus = useCallback(
    async (
      record: QuestionnaireSourceRecordContract,
      nextArchiveStatus: QuestionnaireSourceRecordArchiveStatus,
    ) => {
      setArchivingRecordId(record.id)
      setError(null)
      try {
        const updatedRecord = await archiveQuestionnaireSourceRecord(record.id, nextArchiveStatus)
        setRecords((currentRecords) => {
          if (
            archiveStatus !== 'all' &&
            (updatedRecord.archiveStatus ?? 'active') !== archiveStatus
          ) {
            return currentRecords.filter((item) => item.id !== record.id)
          }
          return currentRecords.map((item) => (item.id === record.id ? updatedRecord : item))
        })
        setDetailRecord((currentRecord) =>
          currentRecord?.id === record.id ? updatedRecord : currentRecord,
        )
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : '無法更新問卷記錄封存狀態。')
      } finally {
        setArchivingRecordId(null)
      }
    },
    [archiveStatus],
  )

  return (
    <section
      className={cn('flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white', className)}
    >
      <div className="shrink-0 border-b border-slate-200 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-slate-950">來源問卷記錄</h3>
            <p className="mt-1 text-xs text-slate-500">
              選擇可建立申請的問卷記錄，欄位會依 binding 自動帶入。
            </p>
          </div>
          <Badge variant="outline">{totalAvailable} 筆可用</Badge>
        </div>
        <div className="mt-3 grid gap-2 md:grid-cols-[minmax(0,1fr)_160px_160px_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={keyword}
              onChange={(event) => setKeyword(event.currentTarget.value)}
              placeholder="搜尋問卷、提交者、答案、申請單號"
              className="pl-9"
            />
          </div>
          <Select
            value={linkStatus}
            onValueChange={(value) => setLinkStatus(value as LinkStatusFilter)}
          >
            <SelectTrigger aria-label="建立狀態">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部記錄</SelectItem>
              <SelectItem value="available">可建立申請</SelectItem>
              <SelectItem value="linked">已建立申請</SelectItem>
              <SelectItem value="unavailable">不可建立</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={archiveStatus}
            onValueChange={(value) => setArchiveStatus(value as ArchiveStatusFilter)}
          >
            <SelectTrigger aria-label="封存狀態">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">未封存</SelectItem>
              <SelectItem value="archived">已封存</SelectItem>
              <SelectItem value="all">全部封存狀態</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            aria-label="重新載入來源問卷記錄"
            title="重新載入來源問卷記錄"
            onClick={() => void loadRecords()}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
          </Button>
        </div>
      </div>
      {error ? (
        <div className="m-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-3">
        <div className="grid gap-3">
          {isLoading && records.length === 0 ? (
            <div className="grid min-h-40 place-items-center text-sm text-slate-500">
              <Loader2 className="mb-2 size-5 animate-spin text-indigo-500" />
              載入問卷記錄...
            </div>
          ) : null}
          {!isLoading && records.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              沒有符合條件的問卷記錄。
            </div>
          ) : null}
          {records.map((record) => {
            const meta = linkStatusMeta[record.linkStatus]
            const Icon = meta.icon
            const isArchived = record.archiveStatus === 'archived'
            const isArchiving = archivingRecordId === record.id
            const selectable = record.linkStatus === 'available' && !isArchived
            const selected = selectedId === record.id
            const primaryAnswer = getPrimaryAnswer(record)
            return (
              <div
                key={record.id}
                className={cn(
                  'rounded-lg border bg-white p-3 text-left transition-colors',
                  selected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200',
                  !selectable && 'opacity-75',
                )}
              >
                <button
                  type="button"
                  disabled={!selectable}
                  aria-disabled={!selectable}
                  aria-pressed={selected}
                  aria-label={`${selectable ? '選擇' : '不可選擇'}問卷記錄：${record.formTitle}，${primaryAnswer.valuePreview || primaryAnswer.questionTitle}`}
                  onClick={() => {
                    if (selectable) onSelect(record)
                  }}
                  title={
                    isArchived
                      ? '這筆問卷記錄已封存，不能建立新客申請。'
                      : record.linkStatus === 'linked'
                        ? '這筆問卷記錄已建立新客申請。'
                        : record.linkStatus === 'unavailable'
                          ? '這筆問卷記錄尚未完成，不能建立新客申請。'
                          : '使用這筆問卷記錄建立新客申請。'
                  }
                  className={cn(
                    'block w-full rounded-md text-left transition-colors',
                    selectable && 'hover:bg-slate-50',
                    !selectable && 'cursor-not-allowed',
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-medium text-slate-950">{record.formTitle}</h4>
                        <Badge variant={meta.variant}>
                          <Icon className="mr-1 size-3" />
                          {meta.label}
                        </Badge>
                        {isArchived ? <Badge variant="secondary">已封存</Badge> : null}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {record.submittedByName ?? '未命名提交者'} · {record.submittedAt}
                      </p>
                    </div>
                    {record.linkedApplicationNumber ? (
                      <Badge variant="outline">{record.linkedApplicationNumber}</Badge>
                    ) : null}
                  </div>
                  <div className="mt-3 rounded-md bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">{primaryAnswer.questionTitle}</div>
                    <div className="mt-1 line-clamp-1 text-sm font-medium text-slate-800">
                      {primaryAnswer.valuePreview || '-'}
                    </div>
                  </div>
                  {!selectable ? (
                    <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                      {getDisabledReason(record)}
                    </div>
                  ) : null}
                </button>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title={`查看 ${record.formTitle} 的問卷詳細資訊`}
                      onClick={() => setDetailRecord(record)}
                    >
                      <Eye className="size-4" />
                      查看問卷詳細資訊
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-label={isArchived ? '取消封存問卷記錄' : '封存問卷記錄'}
                      title={isArchived ? '取消封存這筆問卷記錄' : '封存這筆問卷記錄'}
                      onClick={() =>
                        void updateArchiveStatus(record, isArchived ? 'active' : 'archived')
                      }
                      disabled={isArchiving}
                    >
                      {isArchiving ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : isArchived ? (
                        <ArchiveRestore className="size-4" />
                      ) : (
                        <Archive className="size-4" />
                      )}
                      {isArchived ? '取消封存' : '封存'}
                    </Button>
                  </div>
                  {selected ? (
                    <div className="flex items-center gap-2 text-xs font-medium text-indigo-700">
                      <Badge variant="success">問卷帶入</Badge>
                      已選擇這筆記錄，將依欄位 binding 帶入申請表。
                    </div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </ScrollArea>
      <QuestionnaireSourceRecordDetailDialog
        record={detailRecord}
        open={Boolean(detailRecord)}
        onOpenChange={(open) => {
          if (!open) setDetailRecord(null)
        }}
      />
    </section>
  )
}

function getPrimaryAnswer(record: QuestionnaireSourceRecordContract) {
  return (
    record.answerDetails.find((answer) => answer.mappedTarget === 'companyName') ??
    record.answerDetails.find((answer) => answer.mappedTarget) ??
    record.firstAnswers[0] ?? {
      questionId: record.id,
      questionTitle: '第一個欄位',
      valuePreview: '-',
    }
  )
}

function getDisabledReason(record: QuestionnaireSourceRecordContract) {
  if (record.archiveStatus === 'archived') {
    return '這筆問卷記錄已封存，需取消封存後才能建立新客申請。'
  }
  if (record.linkStatus === 'linked') {
    const statusLabel = record.linkedApplicationStatus
      ? (linkedApplicationStatusLabels[record.linkedApplicationStatus] ??
        record.linkedApplicationStatus)
      : '狀態未提供'
    return `已連結申請 ${record.linkedApplicationNumber ?? record.linkedApplicationId ?? '未命名'}（${statusLabel}）。同一筆問卷記錄只能對應一筆 active 新客申請。`
  }
  if (record.linkStatus === 'unavailable') {
    return '這筆問卷記錄尚未完整送出，需完成問卷後才能建立新客申請。'
  }
  return ''
}
